import { useTheme } from '@backoffice-frontend/patterns'
import {
  CheckCircleSolidIcon,
  CheckmarkIcon,
  WarningIcon,
} from '@moia-dev/pace-icons'
import { Button, Heading, LoadingIndicator, Text } from '@moia-dev/pace-web'
import { useTranslation } from 'react-i18next'

type TelematicsCheckProps = {
  expectedPassengerCount: number
  seatedPassengerCount: number
  onConfirmLessPassengers: VoidFunction
  hasConfirmedLessPassengers: boolean
}

export const PassengerCount = ({
  expectedPassengerCount,
  seatedPassengerCount,
  onConfirmLessPassengers,
  hasConfirmedLessPassengers,
}: TelematicsCheckProps) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const allExpectedPassengerSeated =
    seatedPassengerCount === expectedPassengerCount

  const isMoreSeatedPassengerThanExpected =
    seatedPassengerCount > expectedPassengerCount
  const isLessPassengersSeated = seatedPassengerCount < expectedPassengerCount
  const isAllPassengersSeated = expectedPassengerCount === seatedPassengerCount

  const variant =
    allExpectedPassengerSeated || hasConfirmedLessPassengers
      ? 'success'
      : isMoreSeatedPassengerThanExpected
        ? 'overwrite'
        : 'pending'

  const backgroundColor =
    (variant === 'success' && theme.semantic.ColorSurfaceSuccess) ||
    (variant === 'overwrite' && theme.semantic.ColorWarningAlternative) ||
    (variant === 'pending' && theme.semantic.ColorSurfaceReducedAlternative) ||
    undefined

  const titleColor =
    (variant === 'success' && theme.semantic.ColorSuccessContrast) ||
    (variant === 'overwrite' && theme.semantic.ColorWarningContrast) ||
    (variant === 'pending' && theme.semantic.ColorContentReduced) ||
    undefined

  const captionColor =
    (variant === 'success' && theme.semantic.ColorContentReduced) ||
    (variant === 'overwrite' && theme.semantic.ColorWarningContrast) ||
    (variant === 'pending' && theme.semantic.ColorContentReduced) ||
    undefined

  return (
    <div>
      {/* Header */}
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: theme.spacing(2),
          color: titleColor,
          backgroundColor,
          borderRadius: '8px 8px 0 0',
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: titleColor,
        }}
      >
        <Heading size="md" css={{ color: titleColor }}>
          {isAllPassengersSeated
            ? expectedPassengerCount > 0
              ? t('adFleetMap_cscAllPassengersBoarded_caption')
              : t('adFleetMap_noPassengerOnBoard_label')
            : seatedPassengerCount > expectedPassengerCount
              ? t('adFleetMap_tooManyPassengersBoarded_label')
              : hasConfirmedLessPassengers
                ? t('adFleetMap_trip_acceptedLessPassengers_label')
                : t('adFleetMap_waitingForBoardingPassengers_label')}
        </Heading>

        {variant === 'success' && <CheckCircleSolidIcon />}
        {variant === 'overwrite' && <CheckCircleSolidIcon />}
        {variant === 'pending' && <GreyCircle />}
      </div>

      {/* Boarded passengers */}
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          padding: theme.spacing(1, 2),
          gap: theme.spacing(1),
          border: `1px solid ${theme.semantic.ColorAccessoryDefault}`,
          borderBottom: `1px dashed ${theme.semantic.ColorAccessoryDefault}`,
          borderTop: 'none',
          justifyContent: 'space-between',
        }}
      >
        <div
          css={{
            display: 'flex',
            flexDirection: 'row',
            gap: theme.spacing(1),
          }}
        >
          {variant === 'pending' && <LoadingIndicator size={16} />}
          {variant === 'overwrite' && (
            <WarningIcon css={{ width: 16, height: 16, color: captionColor }} />
          )}
          {variant === 'success' && (
            <CheckmarkIcon css={{ width: 16, height: 16 }} />
          )}

          <Text size="sm" css={{ color: captionColor }}>
            {t('adFleetMap_boarding_boardedPassengers_label')}
          </Text>
        </div>

        {/* Passenger counter */}
        <div
          css={{
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <div
            css={{
              display: 'flex',
              flexDirection: 'row',
              backgroundColor: theme.semantic.ColorSurfaceReducedAlternative,
              borderRadius: '5px',
            }}
          >
            {Array.from(
              { length: expectedPassengerCount + 1 },
              (_, index) => index,
            ).map(count => (
              <div
                key={count}
                css={{
                  alignItems: 'center',
                  padding: theme.spacing(1),
                  position: 'relative',
                  border: 'solid 1px',
                  fontWeight: '700',
                  fontSize: '14px',
                  lineHeight: '17px',
                  borderRadius:
                    seatedPassengerCount === count &&
                    !isMoreSeatedPassengerThanExpected
                      ? '5px'
                      : count === 0
                        ? '5px 0 0 5px'
                        : count === expectedPassengerCount
                          ? '0 5px 5px 0'
                          : '0px',
                  borderColor:
                    seatedPassengerCount === count
                      ? titleColor
                      : theme.semantic.ColorSurfaceReducedAlternative,
                  backgroundColor:
                    seatedPassengerCount === count
                      ? backgroundColor
                      : theme.semantic.ColorSurfaceReducedAlternative,
                  color:
                    seatedPassengerCount === count
                      ? variant === 'success'
                        ? titleColor
                        : theme.semantic.ColorContentDefault
                      : theme.semantic.ColorContentReduced,
                }}
              >
                {count}
              </div>
            ))}
          </div>
          <div
            css={{
              alignItems: 'center',
              padding: theme.spacing(1),
              position: 'relative',
              border: 'solid 1.5px',
              fontWeight: '700',
              fontSize: '14px',
              lineHeight: '17px',
              marginLeft: theme.spacing(1),
              borderRadius: '5px',
              borderColor: isMoreSeatedPassengerThanExpected
                ? titleColor
                : theme.semantic.ColorSurfaceReducedAlternative,
              color: isMoreSeatedPassengerThanExpected
                ? titleColor
                : theme.semantic.ColorContentReduced,
              backgroundColor: isMoreSeatedPassengerThanExpected
                ? backgroundColor
                : theme.semantic.ColorSurfaceReducedAlternative,
            }}
          >
            {`${expectedPassengerCount + 1}+`}
          </div>
        </div>
      </div>

      {/* Continue with less passengers */}
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          padding: theme.spacing(1, 2),
          gap: theme.spacing(1),
          borderRadius: '0 0 8px 8px',
          border: `1px solid ${theme.semantic.ColorAccessoryDefault}`,
          borderTop: 'none',
          justifyContent: 'space-between',
          color:
            !isLessPassengersSeated || hasConfirmedLessPassengers
              ? theme.semantic.ColorContentReduced
              : theme.semantic.ColorContentDefault,
        }}
      >
        {t('adFleetMap_cscContinueLessPassengers_button')}
        <Button
          variant="secondary"
          size="compact"
          disabled={!isLessPassengersSeated || hasConfirmedLessPassengers}
          onClick={onConfirmLessPassengers}
          label={t('adFleetMap_yesContinue_button')}
        />
      </div>
    </div>
  )
}

const GreyCircle = () => (
  <div
    css={theme => ({
      width: 20,
      height: 20,
      background: theme.semantic.ColorSurfaceReduced,
      borderRadius: '100%',
    })}
  />
)
